import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import U2000ActionBtn from '../../u2000Core/components/buttons/U2000ActionBtn';
import U2000Panel from '../../u2000Core/components/layout/U2000Panel';
import { NS } from '../U3207';
import {
  U3207_CATEGORY_TOP_N,
  u3207SortByTokensDesc,
  u3207VisibleRows,
} from '../utils/u3207TableRows';
import { u3207FormatTokenCount } from '../utils/u3207Formatters';

export interface U3207CategorySummaryMetric {
  label: string;
  value: ReactNode;
}

export interface U3207CategoryTableRow {
  id: string;
  name: string;
  tokens: number;
  /** Cellules supplémentaires (après nom + tokens), alignées aux `extraHeaders`. */
  extraCells?: ReactNode[];
  onClick?: () => void;
}

interface U3207CategoryTileProps {
  title: string;
  summaryMetrics?: U3207CategorySummaryMetric[];
  /** En-têtes des colonnes extras (après Nom et Tokens). */
  extraHeaders?: string[];
  rows: U3207CategoryTableRow[];
  emptyLabel?: string;
  topN?: number;
  headerAction?: ReactNode;
}

function U3207CategoryTile({
  title,
  summaryMetrics,
  extraHeaders = [],
  rows,
  emptyLabel,
  topN = U3207_CATEGORY_TOP_N,
  headerAction,
}: U3207CategoryTileProps) {
  const { t, i18n } = useTranslation(NS);
  const locale = i18n.language;
  const [showAll, setShowAll] = useState(false);

  const sorted = useMemo(() => u3207SortByTokensDesc(rows), [rows]);
  const visible = useMemo(
    () => u3207VisibleRows(sorted, topN, showAll),
    [sorted, topN, showAll],
  );
  const canToggle = sorted.length > topN;

  return (
    <U2000Panel title={title} className="pt-6!" variant="outlined">
      {(headerAction || (summaryMetrics && summaryMetrics.length > 0)) ? (
        <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
          {summaryMetrics && summaryMetrics.length > 0 ? (
            <dl className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
              {summaryMetrics.map((m) => (
                <div key={m.label} className="flex items-baseline gap-1.5">
                  <dt className="text-muted-foreground">{m.label}</dt>
                  <dd className="font-medium tabular-nums text-foreground">{m.value}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <span />
          )}
          {headerAction}
        </div>
      ) : null}

      <div className="min-h-0 overflow-x-auto">
        {sorted.length === 0 ? (
          <p className="py-4 text-sm text-muted-foreground">{emptyLabel ?? t('empty')}</p>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">{t('table.name')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('kpi.tokens')}</th>
                {extraHeaders.map((h) => (
                  <th key={h} className="px-3 py-2 text-right font-medium">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => {
                const interactive = Boolean(row.onClick);
                return (
                  <tr
                    key={row.id}
                    className={`border-t border-border ${
                      interactive ? 'cursor-pointer hover:bg-muted/30' : ''
                    }`}
                    onClick={row.onClick}
                    onKeyDown={
                      interactive
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              row.onClick?.();
                            }
                          }
                        : undefined
                    }
                    tabIndex={interactive ? 0 : undefined}
                    role={interactive ? 'button' : undefined}
                  >
                    <td className="px-3 py-2 font-medium text-foreground">{row.name}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-foreground">
                      {u3207FormatTokenCount(row.tokens, locale)}
                    </td>
                    {(row.extraCells ?? []).map((cell, idx) => (
                      <td
                        key={`${row.id}-extra-${idx}`}
                        className="px-3 py-2 text-right tabular-nums text-muted-foreground"
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {canToggle ? (
        <div className="mt-3 flex justify-end border-t border-border pt-3">
          <U2000ActionBtn
            variant="secondary"
            label={
              showAll
                ? t('table.showLess')
                : t('table.showMore', { count: sorted.length - topN })
            }
            handleClick={() => setShowAll((v) => !v)}
            className="px-3! py-1.5! text-xs"
          />
        </div>
      ) : null}
    </U2000Panel>
  );
}

export default U3207CategoryTile;
